import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];

export default function DriverEarnings() {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [annual, setAnnual] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const q = annual ? `year=${year}` : `year=${year}&month=${month}`;
      setData((await api.get(`/driver/earnings?${q}`)).data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [annual, year, month]);

  useEffect(() => { load(); }, [load]);

  const years = data?.years?.length ? data.years : [String(now.getFullYear())];

  return (
    <div data-testid="driver-earnings" className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <div className="no-print flex flex-wrap items-center gap-3">
        <Button data-testid="earnings-back" variant="outline" onClick={() => navigate("/portal/driver")}
          className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
          <ArrowLeft className="mr-2 h-4 w-4" /> My trips
        </Button>
        <Button data-testid="earnings-print" onClick={() => window.print()}
          className="bg-gold text-ink-900 hover:bg-gold-light">
          <Printer className="mr-2 h-4 w-4" /> Print / save PDF
        </Button>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-gold" />}
      </div>

      {error && <p data-testid="earnings-error" className="text-sm text-red-400">{error}</p>}

      <div className="no-print flex flex-wrap items-center gap-2">
        {[[false, "Monthly"], [true, "Full year"]].map(([k, l]) => (
          <button key={l} data-testid={`earnings-view-${l.toLowerCase().replace(" ", "-")}`}
            onClick={() => setAnnual(k)}
            className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest ${
              annual === k ? "border-gold/40 bg-gold/10 text-gold" : "border-ink-600 text-slate-400"}`}>
            {l}
          </button>
        ))}
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger data-testid="earnings-year" className="h-10 w-[110px] border-ink-600 bg-ink-800 text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-ink-600 bg-ink-800 text-slate-100">
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        {!annual && (
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger data-testid="earnings-month" className="h-10 w-[150px] border-ink-600 bg-ink-800 text-slate-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-ink-600 bg-ink-800 text-slate-100">
              {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="rounded-xl border border-ink-600 bg-ink-800 p-6 print:bg-white print:text-black">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Earnings statement</p>
        <h1 className="mt-3 text-2xl font-bold text-slate-50 print:text-black">{data?.chauffeur?.name}</h1>
        <p className="mt-1 font-mono text-xs text-slate-500">
          {data?.period} · {data?.company?.name}
        </p>
        {data?.settlement ? (
          <div data-testid="earnings-settled" className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4">
            <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">Settled</p>
            <p className="mt-1 text-sm text-slate-200 print:text-black">
              {money(data.settlement.amount)} paid on {data.settlement.paid_on} · {data.settlement.method_label}
            </p>
            {data.settlement.reference && (
              <p className="font-mono text-[10px] text-slate-500">Ref {data.settlement.reference}</p>
            )}
          </div>
        ) : (data?.totals?.trips > 0 && (
          <p data-testid="earnings-pending" className="mt-4 font-mono text-[11px] uppercase tracking-widest text-amber-300">
            Payment pending — your operator has not marked this period paid yet
          </p>
        ))}

        <div className="mt-6 grid grid-cols-2 gap-4">
          {[["Trips", data?.totals?.trips ?? 0], ["Base payout", money(data?.totals?.base)],
            ["Tips", money(data?.totals?.tips)], ["Total earned", money(data?.totals?.total)]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-ink-600 bg-ink-900/50 p-4 print:border-slate-300 print:bg-white">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</p>
              <p data-testid={`earnings-${l.toLowerCase().replace(" ", "-")}`} className="mt-1.5 font-display text-xl text-slate-50 print:text-black">{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 space-y-2" data-testid="earnings-trips">
          {(data?.trips || []).map((t) => (
            <div key={t.manifest_id} className="flex items-center justify-between gap-3 border-b border-ink-700/60 pb-2 text-sm">
              <div className="min-w-0">
                <p className="truncate text-slate-200 print:text-black">#{t.number} · {t.customer_name}</p>
                <p className="font-mono text-[11px] text-slate-500">{t.trip_date} · {t.miles.toFixed(1)} mi · {t.basis}</p>
              </div>
              <div className="text-right">
                <p className="text-slate-100 print:text-black">{money(t.total)}</p>
                {t.tip > 0 && <p className="font-mono text-[11px] text-emerald-300">incl. {money(t.tip)} tip</p>}
              </div>
            </div>
          ))}
          {(data?.trips || []).length === 0 && !busy && (
            <p data-testid="earnings-empty" className="py-6 text-center text-sm text-slate-500">
              No completed trips in {data?.period}.
            </p>
          )}
        </div>

        <p className="mt-6 font-mono text-[11px] text-slate-500">
          Lifetime with {data?.company?.name}: {data?.lifetime?.trips ?? 0} trips · {money(data?.lifetime?.total)}
        </p>
      </div>
    </div>
  );
}
